import AccessibleIcon from '@mui/icons-material/Accessible'
import HearingIcon from '@mui/icons-material/Hearing'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Checkbox, Dialog, DialogContent, FormControlLabel } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { BabyIcon, CocktailIcon, FlagIcon } from '../../components/ForEscapeIcons.jsx'
import TextToInput from '../../components/TextToInput/TextToInput'
import { useAuthentificationStore } from '../../contexts/AuthenticationStore'
import { useDayStore } from '../../contexts/DayStore'
import {
  COLOR_VISION_GROUPS,
  COLOR_VISION_ME,
  COLOR_VISION_MG,
  useDayViewSettingsStore,
} from '../../contexts/DayViewSettingsStore'
import { useUsersStore } from '../../contexts/UsersStore'
import {
  BRUSH_COCKTAIL,
  BRUSH_FILMING,
  BRUSH_FILMING_SYMBOL,
  BRUSH_NOSHOW,
} from '../generator/BrushesGenerator/AllBrushes'
import './ModalEditSessionStyles.css'

const ModalEditSession = ({
  isOpen,
  setIsOpen,
  session,
  intervalGroups,
  dayId,
  intervalId,
  sequences = [],
}) => {
  const { user } = useAuthentificationStore(useShallow((state) => ({ user: state.user })))
  const sequence = sequences.find((seq) => seq.sessions.find((sId) => session._id === sId))
  const { updateSessionValue } = useDayStore(
    useShallow((state) => ({ updateSessionValue: state.updateSessionValue }))
  )
  const { users } = useUsersStore(
    useShallow((state) => ({
      users: state.users,
    }))
  )
  const { colorVision } = useDayViewSettingsStore(
    useShallow((state) => ({
      colorVision: state.colorVision,
    }))
  )
  const onClose = () => {
    setIsOpen(false)
  }
  const onBlurMGName = useCallback(
    (search) => {
      const foundUser = Object.values(users)
        .filter((u) => u.state !== 0)
        .find((u) => u.initials.toUpperCase() === search)
      if (foundUser) {
        updateSessionValue(dayId, intervalId, session?._id, 'mg', foundUser._id)
      } else {
        updateSessionValue(dayId, intervalId, session?._id, 'mg', null)
      }
    },
    [users, updateSessionValue, dayId, intervalId, session?._id]
  )

  const toggleLongBreak = useCallback(() => {
    updateSessionValue(dayId, intervalId, session?._id, 'needLongBreak', !session.needLongBreak)
    if (session.needLongBreak)
      updateSessionValue(dayId, intervalId, session?._id, 'longBreakText', '')
  }, [dayId, intervalId, session?._id, session.needLongBreak, updateSessionValue])

  const toggleShortBreak = useCallback(() => {
    updateSessionValue(dayId, intervalId, session?._id, 'needShortBreak', !session.needShortBreak)
    if (session.needShortBreak)
      updateSessionValue(dayId, intervalId, session?._id, 'shortBreakText', '')
  }, [dayId, intervalId, session?._id, session.needShortBreak, updateSessionValue])

  const toggleIgnoreIsPaid = useCallback(() => {
    updateSessionValue(dayId, intervalId, session?._id, 'ignoreIsPaid', !session.ignoreIsPaid)
  }, [dayId, intervalId, session?._id, session.ignoreIsPaid, updateSessionValue])

  const onEditNote = useCallback(
    (nextNote) => {
      updateSessionValue(dayId, intervalId, session?._id, 'note', nextNote)
    },
    [updateSessionValue, dayId, intervalId, session?._id]
  )
  const onEditLongBreakText = useCallback(
    (nextLongBreakText) => {
      updateSessionValue(dayId, intervalId, session?._id, 'longBreakText', nextLongBreakText)
    },
    [updateSessionValue, dayId, intervalId, session?._id]
  )
  const onEditShortBreakText = useCallback(
    (nextShortBreakText) => {
      updateSessionValue(dayId, intervalId, session?._id, 'shortBreakText', nextShortBreakText)
    },
    [updateSessionValue, dayId, intervalId, session?._id]
  )

  const sessionGroup = useMemo(
    () => intervalGroups.find((g) => g.email === session?.booking?.email),
    [session, intervalGroups]
  )
  let bgColor = null
  const initials = session?.mg ? users[session.mg]?.initials : ''
  const mgColor = users[session.mg]?.color || null
  if (colorVision === COLOR_VISION_MG) {
    bgColor = mgColor
  }
  if (colorVision === COLOR_VISION_ME && user?._id === session.mg) {
    bgColor = mgColor
  }
  if (colorVision === COLOR_VISION_GROUPS && sessionGroup) {
    bgColor = sessionGroup.color
  }
  if (!session?.available && initials.length === 0) {
    bgColor = 'var(--mui-palette-notavailable-default)'
  }
  if (session?.booking?.isClosing && initials.length === 0) {
    bgColor = 'var(--mui-palette-isclosing-default)'
  }
  let isFermetureWithMG = false
  if (!session.available && initials.length > 0) {
    isFermetureWithMG = 'notAvailable'
  }
  if (session?.booking?.isClosing && initials.length > 0) {
    isFermetureWithMG = 'isClosing'
  }
  const blackColor = bgColor !== null ? 'black ' : ''
  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent
        className={`modalEditWrapper ${
          isFermetureWithMG ? `diagonal diagonal-${isFermetureWithMG}` : null
        }`}
        style={{ backgroundColor: bgColor }}
      >
        <TextToInput
          id={session?._id}
          bgColor={bgColor}
          className={`mgText ${blackColor}`}
          initValue={initials}
          textValue={
            <>
              {initials}
              {session?.booking?.nbPlayers > 0 && <> / {session?.booking?.nbPlayers}</>}
            </>
          }
          shouldUppercase
          onBlur={onBlurMGName}
        />
        <TextToInput
          bgColor={bgColor}
          className={`sessionNote ${blackColor}`}
          initValue={session?.note}
          onBlur={onEditNote}
          textValue={<>Note : {session?.note ? session?.note : '  '}</>}
        />
        {session.needLongBreak && (
          <TextToInput
            bgColor={bgColor}
            className={`longBreakText ${blackColor}`}
            initValue={session?.longBreakText}
            onBlur={onEditLongBreakText}
            textValue={session?.longBreakText ? session?.longBreakText : '-'}
          />
        )}
        <div
          onClick={toggleLongBreak}
          className={`longBreak ${session.needLongBreak ? 'active' : 'inactive'}`}
        />
        {session.needShortBreak && (
          <TextToInput
            bgColor={bgColor}
            className={`shortBreakText ${blackColor}`}
            initValue={session?.shortBreakText}
            onBlur={onEditShortBreakText}
            textValue={session?.shortBreakText ? session?.shortBreakText : '-'}
          />
        )}
        <div
          onClick={toggleShortBreak}
          className={`shortBreak ${session.needShortBreak ? 'active' : 'inactive'}`}
        />
        <div className="bottomLeft">
          {sequence && <div className={`sequenceLetter ${blackColor}`}>{sequence.letter}</div>}
          <MonetizationOnIcon
            className={`icon canClick ${blackColor} ${
              session.booking && !session?.booking?.isPaid ? 'active' : 'inactive'
            }`}
          />
          <FormControlLabel
            className={`${blackColor}`}
            onChange={toggleIgnoreIsPaid}
            control={
              <Checkbox
                size="small"
                className={`${blackColor}`}
                checked={session.ignoreIsPaid || false}
              />
            }
            label="Ignore"
          />
        </div>
        <div className={`roomSpecificities ${blackColor}`}>
          {session?.booking?.hasChild && <BabyIcon className="icon" />}
          {session?.booking?.hasDisabledPerson && <AccessibleIcon className="icon" />}
          {session?.booking?.hasDeaf && <HearingIcon className="icon" />}
          {session?.booking?.isEnglish && <FlagIcon className="icon" />}
          {session?.events.map((e) => {
            switch (e) {
              case BRUSH_NOSHOW:
                return (
                  <span key={e} className="eventSymbol">
                    <VisibilityOffIcon />
                  </span>
                )
              case BRUSH_FILMING:
                return (
                  <span key={e} className="eventSymbol">
                    {BRUSH_FILMING_SYMBOL}
                  </span>
                )
              case BRUSH_COCKTAIL:
                return (
                  <span key={e} className="eventSymbol">
                    <CocktailIcon />
                  </span>
                )
            }
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default ModalEditSession
